import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';

/**
 * A menu anchored to an element, the app's row-actions style. Items:
 * {label, onClick, danger?, disabled?, checked?} or null for a divider.
 */
export default function Menu({ anchor, items, onClose, width = 176 }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    const r = anchor?.getBoundingClientRect();
    if (!r) return;
    setPos({ top: Math.min(window.innerHeight - 8, r.bottom + 4), left: Math.max(8, Math.min(window.innerWidth - width - 8, r.right - width)) });
  }, [anchor, width]);

  useEffect(() => { ref.current?.querySelector('button:not(:disabled)')?.focus(); }, []);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current?.contains(e.target) || anchor?.contains(e.target)) return; onClose(); };
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); anchor?.focus?.(); return; }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const els = Array.from(ref.current?.querySelectorAll('button:not(:disabled)') || []);
      if (!els.length) return;
      e.preventDefault();
      const i = els.indexOf(document.activeElement);
      els[(i + (e.key === 'ArrowDown' ? 1 : -1) + els.length) % els.length].focus();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onClose, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [anchor, onClose]);

  return createPortal(
    <div ref={ref} role="menu" className="fixed z-[10000] rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] py-1 shadow-lg" style={{ top: pos.top, left: pos.left, width }}>
      {items.map((it, i) => (it == null ? (
        <div key={`d${i}`} className="my-1 border-t border-[color:var(--border-subtle)]" />
      ) : (
        <button key={it.label} type="button" role={it.checked != null ? 'menuitemradio' : 'menuitem'} aria-checked={it.checked != null ? it.checked : undefined} disabled={it.disabled}
          onClick={() => { onClose(); it.onClick(); }}
          className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-[12.5px] disabled:opacity-50 ${it.danger ? 'text-[color:var(--feedback-error)] hover:bg-[color:var(--feedback-error-bg)]' : 'text-[color:var(--text-primary)] hover:bg-surface-hover'}`}>
          {it.label}
          {it.checked ? <Check className="h-3.5 w-3.5 shrink-0" aria-hidden /> : null}
        </button>
      )))}
    </div>,
    document.body,
  );
}
